/**
 * LOD0 Brand Image texture cache (browser resource lifecycle authority).
 *
 * Decodes each brand image AT MOST ONCE per content revision and reuses the
 * decoded <img> across frames. Replacement (new revision) invalidates and
 * disposes the previous resource; removal / no-longer-visible identities are
 * disposed. A failed decode is NOT retried until the revision changes (no
 * per-frame retry storm). No Object URLs are created here (we load the public
 * content URL directly), so there is nothing to leak.
 *
 * Cache-busting is DETERMINISTIC: the content URL carries `?v=<revision>` and
 * the backend ETag is `"<sha256>:<revision>"`, so a new revision is a new URL
 * (never a random timestamp) and the browser HTTP cache stays coherent.
 */
import type { BrandImageMeta } from "../lib/brand-image-api";
import { brandImageContentUrl } from "../lib/brand-image-api";

export type TextureStatus = "loading" | "ready" | "error";

interface Entry {
  revision: number;
  sha256: string;
  status: TextureStatus;
  image: HTMLImageElement | null;
}

export interface TextureCacheOptions {
  /** Injected for tests; defaults to the browser HTMLImageElement. */
  createImage?: () => HTMLImageElement;
  /** Injected for tests; defaults to the canonical cache-busted content URL. */
  urlFor?: (identityId: string, revision: number) => string;
  /** Called whenever a texture transitions to ready (so the viewer repaints). */
  onReady?: () => void;
}

export class BrandImageTextureCache {
  private readonly entries = new Map<string, Entry>();
  private readonly createImage: () => HTMLImageElement;
  private readonly urlFor: (identityId: string, revision: number) => string;
  private readonly onReady?: () => void;
  private _contentRequests = 0;

  constructor(opts: TextureCacheOptions = {}) {
    this.createImage =
      opts.createImage ?? (() => new Image() as unknown as HTMLImageElement);
    this.urlFor = opts.urlFor ?? brandImageContentUrl;
    this.onReady = opts.onReady;
  }

  /** Total content (image byte) requests issued — one decode per revision. */
  get contentRequestCount(): number {
    return this._contentRequests;
  }

  /** Number of live cache entries (for tests / instrumentation). */
  get size(): number {
    return this.entries.size;
  }

  /** Reconcile the cache to EXACTLY the current metadata set. */
  sync(metas: readonly BrandImageMeta[]): void {
    const present = new Set<string>();
    for (const m of metas) {
      present.add(m.identity_id);
      const cur = this.entries.get(m.identity_id);
      const revChanged = !cur || cur.revision !== m.revision || cur.sha256 !== m.sha256;
      if (revChanged) {
        if (cur) this.dispose(cur);
        this.load(m);
      }
    }
    // Dispose entries no longer present (removed / out of view).
    for (const [id, entry] of [...this.entries.entries()]) {
      if (!present.has(id)) {
        this.dispose(entry);
        this.entries.delete(id);
      }
    }
  }

  private load(meta: BrandImageMeta): void {
    const img = this.createImage();
    const entry: Entry = {
      revision: meta.revision,
      sha256: meta.sha256,
      status: "loading",
      image: img,
    };
    this.entries.set(meta.identity_id, entry);
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      if (this.entries.get(meta.identity_id) !== entry) return; // superseded
      entry.status = "ready";
      this.onReady?.();
    };
    img.onerror = () => {
      if (this.entries.get(meta.identity_id) !== entry) return; // superseded
      entry.status = "error";
      entry.image = null; // no per-frame retry; wait for a new revision
    };
    this._contentRequests += 1;
    img.src = this.urlFor(meta.identity_id, meta.revision);
  }

  private dispose(entry: Entry): void {
    const img = entry.image;
    if (img) {
      img.onload = null;
      img.onerror = null;
      // Cancel any in-flight decode and release the resource.
      try {
        img.src = "";
      } catch {
        /* jsdom/happy-dom may throw on empty src — ignore */
      }
      entry.image = null;
    }
  }

  /** A decoded, ready image for `identityId`, or null (loading / error / absent). */
  get(identityId: string): HTMLImageElement | null {
    const e = this.entries.get(identityId);
    return e && e.status === "ready" ? e.image : null;
  }

  statusOf(identityId: string): TextureStatus | null {
    return this.entries.get(identityId)?.status ?? null;
  }

  /** Dispose everything (unmount / context loss). */
  clear(): void {
    for (const entry of this.entries.values()) this.dispose(entry);
    this.entries.clear();
  }
}
